<script setup lang="ts">
// Wrapping filter tabs that replace the old side-scrolling strip.
//  - Desktop: tabs wrap across up to 3 rows. If more tabs exist than fit, the
//    visible set is trimmed and an "…" tab is appended; tapping it expands the
//    extra rows in place. Selecting any filter collapses back to 3 rows.
//  - Mobile (<=900px): a single row with the "…" tab at the end; tapping it
//    opens a modal listing every filter. Selecting a filter (or closing the
//    modal) dismisses it.
// Fit is computed by simulating flex-wrap from measured button widths (a
// hidden measuring layer renders every tab with the same .tab styles), so the
// cutoff is exact for any label set / container width and re-runs on resize.
interface Tab { key: string; label: string }
const props = defineProps<{ tabs: Tab[]; modelValue: string }>()
const emit = defineEmits<{ (e: 'update:modelValue', v: string): void }>()

const wrapEl = ref<HTMLElement | null>(null)
const measureEl = ref<HTMLElement | null>(null)
const expanded = ref(false)
const modalOpen = ref(false)
const isMobile = ref(false)
// Gate the body-Teleport until after mount so it never teleports during SSR
// hydration (Teleport-to-body is the classic source of the "nextSibling of null"
// hydration crash — the WebView's <body> differs from the SSR HTML because RN
// injects its bridge script there).
const isMounted = ref(false)
// number of tabs shown while collapsed; Infinity until first measure (SSR-safe:
// the row is CSS-clamped so an unmeasured first paint can't explode the layout)
const cutoff = ref<number>(Infinity)

const GAP = 8
const overflow = computed(() => props.tabs.length > cutoff.value)
const visibleTabs = computed<Tab[]>(() => {
  if (expanded.value || !overflow.value) return props.tabs
  const vis = props.tabs.slice(0, cutoff.value)
  // keep the active filter visible while collapsed: swap it in for the last slot
  if (props.modelValue && !vis.some(t => t.key === props.modelValue)) {
    const act = props.tabs.find(t => t.key === props.modelValue)
    if (act && vis.length) vis[vis.length - 1] = act
  }
  return vis
})

function rowsFor(widths: number[], containerW: number): number {
  let rows = 1, x = 0
  for (const w of widths) {
    const need = x === 0 ? w : x + GAP + w
    if (need <= containerW) x = need
    else { rows++; x = Math.min(w, containerW) }
  }
  return rows
}

function recompute() {
  const wrap = wrapEl.value, meas = measureEl.value
  if (!wrap || !meas) return
  const containerW = wrap.clientWidth
  if (!containerW) return
  const btns = Array.from(meas.children) as HTMLElement[]
  if (btns.length < props.tabs.length + 1) return
  const widths = btns.slice(0, props.tabs.length).map(b => b.offsetWidth)
  const ellW = btns[btns.length - 1].offsetWidth || 40
  const maxRows = isMobile.value ? 1 : 3
  if (rowsFor(widths, containerW) <= maxRows) { cutoff.value = props.tabs.length; return }
  // longest prefix that, with the active tab swapped into the last slot when
  // it sits beyond the prefix, still fits alongside the "…" tab
  const activeIdx = props.tabs.findIndex(t => t.key === props.modelValue)
  let k = 0
  for (let i = 1; i <= widths.length; i++) {
    const seq = widths.slice(0, i)
    if (activeIdx >= i) seq[seq.length - 1] = widths[activeIdx]
    seq.push(ellW)
    if (rowsFor(seq, containerW) <= maxRows) k = i
    else break
  }
  cutoff.value = Math.max(1, k)
}

function pick(key: string) {
  emit('update:modelValue', key)
  // selecting a filter collapses the expanded rows / closes the mobile modal
  expanded.value = false
  modalOpen.value = false
  nextTick(recompute)
}
function onMore() {
  if (isMobile.value) modalOpen.value = true
  else expanded.value = true
}

let ro: ResizeObserver | null = null
let mq: MediaQueryList | null = null
const onMq = () => {
  isMobile.value = !!mq?.matches
  if (!isMobile.value) modalOpen.value = false
  nextTick(recompute)
}
onMounted(() => {
  isMounted.value = true   // safe to teleport now (post-hydration)
  mq = window.matchMedia('(max-width: 900px)')
  isMobile.value = mq.matches
  mq.addEventListener('change', onMq)
  ro = new ResizeObserver(() => recompute())
  if (wrapEl.value) ro.observe(wrapEl.value)
  nextTick(recompute)
})
onBeforeUnmount(() => {
  ro?.disconnect()
  mq?.removeEventListener('change', onMq)
})
watch(() => props.tabs, () => nextTick(recompute), { deep: true })
</script>

<template>
  <div ref="wrapEl" class="ftabs">
    <!-- hidden measuring layer: every tab + the "…" tab with real .tab styles -->
    <div ref="measureEl" class="ftabs-measure" aria-hidden="true">
      <span v-for="t in tabs" :key="'m-' + t.key" class="tab">{{ t.label }}</span>
      <span class="tab more">…</span>
    </div>

    <div class="ftabs-row" :class="{ clamped: !expanded, mobile: isMobile }">
      <button v-for="t in visibleTabs" :key="t.key" class="tab"
        :class="{ active: modelValue === t.key }" @click="pick(t.key)">{{ t.label }}</button>
      <button v-if="overflow && !expanded" class="tab more" aria-label="show all filters"
        @click="onMore">…</button>
      <button v-if="overflow && expanded" class="tab more" aria-label="show fewer filters"
        @click="expanded = false">‹ less</button>
    </div>

    <!-- mobile: all filters in a modal. Teleport disabled until mounted so the
         closed modal hydrates inline (matching SSR) instead of teleporting into
         <body> during hydration. -->
    <Teleport to="body" :disabled="!isMounted">
      <div v-if="modalOpen" class="ftabs-scrim" @click.self="modalOpen = false">
        <div class="ftabs-sheet" role="dialog" aria-label="filters">
          <div class="ftabs-sheet-head">
            <span>filters</span>
            <button class="ftabs-close" aria-label="close" @click="modalOpen = false">✕</button>
          </div>
          <div class="ftabs-sheet-body">
            <button v-for="t in tabs" :key="'mm-' + t.key" class="tab"
              :class="{ active: modelValue === t.key }" @click="pick(t.key)">{{ t.label }}</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.ftabs { position: relative; padding-bottom: 14px; }
.ftabs-measure {
  position: absolute; left: 0; top: 0; height: 0; overflow: hidden;
  visibility: hidden; pointer-events: none;
  display: flex; flex-wrap: nowrap; gap: 8px; width: max-content;
}
.ftabs-row { display: flex; flex-wrap: wrap; gap: 8px; }
/* pre-measure safety clamp: 3 rows desktop / 1 row mobile (a row is ~37px tab
   + 8px gap); once the cutoff is measured the content fits inside this anyway */
.ftabs-row.clamped { overflow: hidden; max-height: 127px; }
.ftabs-row.clamped.mobile { max-height: 37px; }
.tab.more { color: var(--accent); font-weight: 700; letter-spacing: 1px; }

.ftabs-scrim {
  position: fixed; inset: 0; z-index: 200; background: rgba(0, 0, 0, 0.6);
  display: flex; align-items: flex-end; justify-content: center;
}
.ftabs-sheet {
  width: 100%; max-height: 75vh; overflow-y: auto;
  background: var(--panel); border: 1px solid var(--line);
  border-radius: 16px 16px 0 0; padding: 14px 16px 22px;
}
.ftabs-sheet-head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
  color: var(--muted); margin-bottom: 12px;
}
.ftabs-close {
  background: none; border: none; color: var(--muted); font-size: 16px;
  cursor: pointer; padding: 4px 8px;
}
.ftabs-sheet-body { display: flex; flex-wrap: wrap; gap: 8px; }
</style>
